<script setup lang="ts">
import { Icon } from '@iconify/vue';
import { useDialog } from './composables';
import { layouts } from './layouts';
import { onMounted, onUnmounted, ref, shallowRef, type Component } from 'vue';
import { useRouter } from 'vue-router';
import { capitalize } from './utils/string';
import ToastContainer from './components/ToastContainer.vue';

const layout = shallowRef<Component | string>('div');
const router = useRouter();
router.afterEach((to) => {
    const layoutName = capitalize((to.meta.layout as string) || 'panel') + 'Layout';
    layout.value = layouts[layoutName as keyof typeof layouts] || 'div';
});
const isOnline = ref(false);
const dialogRef = ref<HTMLDialogElement | null>(null);
const dialog = useDialog();

onMounted(() => {
    isOnline.value = navigator.onLine;
    window.addEventListener('online', () => (isOnline.value = true));
    window.addEventListener('offline', () => (isOnline.value = false));
    dialog.register(dialogRef.value!);
    dialogRef.value?.addEventListener('close', dialog.handleClose);
});
onUnmounted(() => {
    window.removeEventListener('online', () => (isOnline.value = true));
    window.removeEventListener('offline', () => (isOnline.value = false));
    dialogRef.value?.removeEventListener('close', dialog.handleClose);
});
</script>

<template>
    <component :is="layout || 'div'">
        <router-view />
    </component>

    <!-- <Teleport to="body">
    </Teleport> -->
    <ToastContainer />

    <dialog
        ref="dialogRef"
        closedby="closerequest"
        class="rounded-lg shadow-xl bg-white p-6 w-full sm:max-w-lg m-auto backdrop:bg-gray-500/50 backdrop:backdrop-blur-[2px]"
    >
        <div class="sm:flex sm:items-start">
            <div
                class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10"
                :class="{ 'bg-red-100': dialog.payload.type == 'error', 'bg-green-100': dialog.payload.type == 'success' }"
            >
                <Icon icon="fluent:warning-24-regular" class="block h-6 w-6 text-red-600" v-if="dialog.payload.type == 'error'" />
                <Icon icon="fluent:checkmark-24-regular" class="block h-6 w-6 text-green-700" v-if="dialog.payload.type == 'success'" />
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-bold text-gray-900">{{ dialog.payload.title }}</h3>
                <div class="mt-2">
                    <p class="text-sm text-gray-500">
                        {{ dialog.payload.description }}
                    </p>
                </div>
            </div>
        </div>
        <form method="dialog" class="block w-full mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-2">
            <button
                type="submit"
                value="confirm"
                class="btn w-full sm:w-auto mt-3 sm:mt-0"
                :class="{
                    'btn-default': dialog.payload.mode == 'alert',
                    'btn-success': dialog.payload.mode == 'confirm' && dialog.payload.type == 'success',
                    'btn-danger': dialog.payload.mode == 'confirm' && dialog.payload.type == 'error',
                }"
            >
                {{ dialog.payload.okText }}
            </button>
            <button type="submit" value="cancel" v-if="dialog.payload.mode == 'confirm'" class="btn btn-default w-full sm:w-auto mt-3 sm:mt-0">Cancel</button>
        </form>
    </dialog>
</template>
